import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Lock, ShieldCheck, Upload, Building2, KeyRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CarregandoBloco, CarregandoFormulario } from "@/components/carregando";

import FormEmpresaFiscal from "./FormEmpresaFiscal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const API = "/api";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

/**
 * Configurações → Sistema → Integração → "NFe Fácil".
 *
 * Mora junto das outras integrações (iFood, Mercado Pago, UP Entrega) porque
 * conectar serviço externo é sempre no mesmo lugar. O nome exposto ao lojista
 * é "NFe Fácil" — o provedor por trás é detalhe de implementação nosso.
 *
 * Passo a passo de habilitação da loja como emitente. É sequencial de verdade:
 * sem empresa cadastrada não dá pra subir certificado, sem certificado e CSC
 * não dá pra ativar. Por isso cada passo trava o seguinte em vez de deixar o
 * lojista preencher tudo e descobrir o erro na primeira venda.
 */

function Passo({ n, titulo, descricao, feito, travado, children }) {
  return (
    <div className={`bg-card rounded-2xl border shadow-sm overflow-hidden ${travado ? "opacity-55" : ""}`}>
      <div className="px-5 py-3.5 border-b bg-muted/30 flex items-start gap-3">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold ${
            feito
              ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {feito ? <Check className="w-5 h-5" /> : travado ? <Lock className="w-4 h-4" /> : n}
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-sm font-semibold block">{titulo}</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">{descricao}</p>
        </div>
      </div>
      {!travado && <div className="p-4">{children}</div>}
    </div>
  );
}

function Campo({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[11px] font-medium">{label}</Label>
      {children}
    </div>
  );
}

const FORM_VAZIO = {
  cnpj: "", razao_social: "", nome_fantasia: "", inscricao_estadual: "", email: "",
  logradouro: "", numero: "", complemento: "", bairro: "", municipio: "", uf: "", cep: "",
};

export default function ConfiguracaoFiscal() {
  const [cfg, setCfg] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [cert, setCert] = useState({ arquivo: null, senha: "" });
  const [sefaz, setSefaz] = useState({ csc: "", token_id: "", serie: "1" });

  // `carregando` só vale na 1ª leitura: recarregar depois de salvar o
  // certificado não troca o formulário pelo carregamento (25/10/2026)
  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/fiscal/settings`, auth());
      setCfg(data);
      // Pré-preenche com o cadastro que a loja já tem em Configurações →
      // Empresa. É só sugestão: os dados da nota precisam bater com o cartão
      // CNPJ, então tudo continua editável. Só preenche campo ainda vazio,
      // pra não sobrescrever o que o lojista já corrigiu na tela.
      if (data.sugestao_empresa) {
        setForm((atual) => {
          const merged = { ...atual };
          for (const [k, v] of Object.entries(data.sugestao_empresa)) {
            if (v && !merged[k]) merged[k] = v;
          }
          return merged;
        });
      }
      setSefaz({
        csc: data.csc || "",
        token_id: data.token_id || "",
        serie: data.serie || "1",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível carregar a configuração fiscal");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const cadastrarLoja = async () => {
    const faltando = ["cnpj", "razao_social", "nome_fantasia", "logradouro", "numero", "bairro", "municipio", "uf", "cep"]
      .filter((k) => !form[k]?.trim());
    if (faltando.length) {
      toast.error("Preencha todos os campos obrigatórios do endereço e da empresa");
      return;
    }
    setSalvando(true);
    try {
      const { data } = await axios.post(`${API}/fiscal/onboarding`, form, auth());
      setCfg(data);
      toast.success("Loja conectada ao NFe Fácil");
    } catch (e) {
      // 422 do schema vem como lista [{msg}]; "Value error, " é ruído técnico.
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => String(x.msg || "").replace(/^Value error, /, "")).join(" · ") : d;
      toast.error(msg || "Erro ao cadastrar a loja");
    } finally {
      setSalvando(false);
    }
  };

  const enviarCertificado = async () => {
    if (!cert.arquivo || !cert.senha) {
      toast.error("Selecione o arquivo .pfx e informe a senha");
      return;
    }
    setSalvando(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", cert.arquivo);
      fd.append("senha", cert.senha);
      await axios.post(`${API}/fiscal/certificado`, fd, {
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Certificado enviado");
      setCert({ arquivo: null, senha: "" });
      await carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar o certificado");
    } finally {
      setSalvando(false);
    }
  };

  // Envia SÓ o que mudou. Antes cada ação reenviava o formulário inteiro, e um
  // ambiente antigo guardado no estado podia voltar por cima do escolhido.
  const salvar = async (parcial, mensagem = "Configuração salva") => {
    setSalvando(true);
    try {
      const { data } = await axios.put(`${API}/fiscal/settings`, parcial, auth());
      setCfg(data);
      toast.success(mensagem);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    // Carregamento com cards (25/10/2026): os passos e o formulário piscando
    return (
      <div className="space-y-4" data-e2e="fiscal-config-carregando">
        <CarregandoBloco altura="h-28" />
        <CarregandoFormulario campos={5} />
      </div>
    );
  }

  // Empresa sem credencial NAO emite — o passo 1 so esta pronto com as duas
  // coisas. Marcar como feito so pelo company_id escondia loja quebrada.
  const temEmpresa = !!cfg?.spedy_company_id && !!cfg?.tem_credencial;
  const empresaSemCredencial = !!cfg?.spedy_company_id && !cfg?.tem_credencial;
  const temCert = !!cfg?.certificado_validade;
  const temCsc = !!cfg?.csc && !!cfg?.token_id;
  const ativa = !!cfg?.emissao_ativa;
  // O ambiente (simulação/homologação/produção) NÃO aparece pro lojista —
  // decisão do dono (24/09/2026): é definido só pelo Núcleo, no servidor. A
  // tela só o lê pra saber se o passo 4 pode ser ligado sem certificado.
  const simulando = (cfg?.ambiente || "sandbox") === "simulacao";

  return (
    <div className="space-y-4">
      <Passo
        n={1}
        titulo="Cadastrar a loja como emitente"
        descricao={temEmpresa ? `Conectada · ${cfg.spedy_company_id}` : "Dados que vão no cabeçalho da nota fiscal."}
        feito={temEmpresa}
      >
        {empresaSemCredencial ? (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 rounded-xl p-4 text-sm">
            <p className="font-semibold text-red-700 dark:text-red-300">
              Empresa criada na Spedy, mas sem credencial.
            </p>
            <p className="text-[12px] text-red-700/80 dark:text-red-400/80 mt-1.5 leading-relaxed">
              O provedor mostra a chave da empresa uma única vez e não tem como recuperá-la depois.
              Sem ela esta loja não emite nota. Para resolver: exclua a empresa
              <span className="font-mono"> {cfg?.spedy_company_id} </span>
              no painel do provedor e conecte de novo por aqui.
            </p>
          </div>
        ) : temEmpresa ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Loja já cadastrada. Para trocar de empresa, fale com o suporte — a credencial é emitida uma vez só.
          </p>
        ) : (
          <FormEmpresaFiscal form={form} setForm={setForm} onSubmit={cadastrarLoja} salvando={salvando} />
        )}
      </Passo>

      <Passo
        n={2}
        titulo="Certificado digital A1"
        descricao={temCert ? `Enviado · validade ${cfg.certificado_validade}` : "Arquivo .pfx do e-CNPJ da loja. É ele que assina cada nota."}
        feito={temCert}
        travado={!temEmpresa}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <Campo label="Arquivo .pfx" className="sm:col-span-1">
            <Input autoComplete="off" type="file" accept=".pfx,.p12" onChange={(e) => setCert({ ...cert, arquivo: e.target.files?.[0] || null })} className="h-9 text-sm" />
          </Campo>
          <Campo label="Senha do certificado">
            <Input type="password" autoComplete="new-password" value={cert.senha} onChange={(e) => setCert({ ...cert, senha: e.target.value })} className="h-9 text-sm" />
          </Campo>
          <Button onClick={enviarCertificado} disabled={salvando} variant="outline" className="h-9">
            <Upload className="w-4 h-4 mr-2" /> Enviar
          </Button>
        </div>
        <p className="text-[10.5px] text-muted-foreground mt-2.5">
          O certificado vence em um ano. Quando vencer, a emissão para — vale anotar a data.
        </p>
      </Passo>

      <Passo
        n={3}
        titulo="CSC e token da SEFAZ"
        descricao="Gerados no portal da SEFAZ do estado, depois do credenciamento como emissor de NFC-e. Sem eles o QR Code não é assinado."
        feito={temCsc}
        travado={!temEmpresa}
      >
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Campo label="CSC" className="sm:col-span-2">
            <Input autoComplete="off" value={sefaz.csc} onChange={(e) => setSefaz({ ...sefaz, csc: e.target.value })} className="h-9 text-sm font-mono" />
          </Campo>
          <Campo label="ID do token">
            <Input autoComplete="off" value={sefaz.token_id} onChange={(e) => setSefaz({ ...sefaz, token_id: e.target.value })} placeholder="000001" className="h-9 text-sm font-mono" />
          </Campo>
          <Campo label="Série">
            <Input autoComplete="off" value={sefaz.serie} onChange={(e) => setSefaz({ ...sefaz, serie: e.target.value })} className="h-9 text-sm font-mono" />
          </Campo>
        </div>
        <Button onClick={() => salvar({ csc: sefaz.csc, token_id: sefaz.token_id, serie: sefaz.serie })} disabled={salvando} variant="outline" className="h-9 mt-3">
          <KeyRound className="w-4 h-4 mr-2" /> Salvar
        </Button>
      </Passo>

      <Passo
        n={4}
        titulo="Ativar a emissão"
        descricao="Com isso ligado, cada venda concluída gera nota automaticamente."
        feito={ativa}
        travado={!simulando && (!temEmpresa || !temCsc)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className={`w-5 h-5 ${ativa ? "text-emerald-500" : "text-muted-foreground"}`} />
            <p className="text-sm font-medium">{ativa ? "Emissão ativa" : "Emissão desligada"}</p>
          </div>
          <Switch
            checked={ativa}
            disabled={salvando}
            onCheckedChange={(v) => salvar({ emissao_ativa: v }, v ? "Emissão ativada" : "Emissão desligada")}
          />
        </div>
      </Passo>
    </div>
  );
}
